import {
  AdminDetailByRoster,
  AppSetting,
  AuthoritiesResponse,
  BackendVersions,
  BatchFacilitiesResponse,
  ClientGroup,
  Clients,
  DiagnosisCodeResponse, DmcsGenerateDocumentResponseContext,
  DropdownItemResponse,
  DropdownItemType,
  EventConsumer,
  FeatureToggleResponse,
  GroupConfiguration,
  IncompleteAdmissionCRFsResponse,
  IncompleteDischargeCRFsResponse,
  LoginResponse,
  Organization,
  OrganizationConfiguration,
  PdmpCredentials, ReferralAttachmentResponse,
  RosterPermissions,
  RosterResponse,
  Rosters, SecureEmailTemplate,
  SurveyJSLicenseKey,
  Unit,
  UpdateGroupConfigurationRequest,
  UpdateOrganizationConfiguration,
  UpdatePdmpCredentialsRequest,
  User,
  UserAccessTemplateResponse,
  UserPatchRequest,
  UserProfileRequest,
  UserRequest,
  UserRequestNew,
  Users,
  WalkMeData
} from "@/api/dto/dto";
import axios, {AxiosError, HttpStatusCode} from "axios";
import {ClientProfile, ClientProfileResponse, ClientProfileUpdate, InsurancePlan} from "@/api/dto/client-profile";
import {Value} from "patient-ping-remedy/packages/dropdown-list/lib/DropdownList";
import Helpers from "@/helpers/helpers";
import {NotificationResponse, NotificationSettingsDto} from "@/api/dto/notification";
import NotificationHelpers from "@/helpers/notification_helpers";
import DisplayHelpers from "@/helpers/display_helpers";
import {getThothOverride} from "@/helpers/thoth_override_helpers";
import {DischargeDisposition, EncounterClient, Insurance, SearchFacility} from "@/api/dto/encounter";
import {PatientResponse} from "@/api/dto/pdmp";
import {
  CancelAppointmentRequest,
  CareJourneyDetailsResponse,
  CareJourneyFilters,
  CareJourneyRequest,
  CareJourneyResponses,
  ConfirmPreReviewRequest,
  RejectPreReviewRequest,
  ReviewDataEditRequest,
  AdditionalEvent,
  ScheduleAppointmentRequest,
  ScheduleAppointmentResponse,
  SealAppointmentRequest,
  TaskSummaryRequest,
  TaskSummaryResponse
} from "@/api/dto/care-journey";
import {
  BulkUserUploadData,
  BulkUserUploadResponse,
  DiagnosisCodesUpdateResponse,
  ScanRosterResponse,
  SendSmsResponse,
  SendSmsResult
} from "@/api/dto/tools";
import {MixpanelEvent} from "@/api/dto/mixpanel";
import {StateConfig} from "@/api/dto/state-config";
import {
  MessageAttachment,
  MessageThread,
  MessageThreadPatch,
  MessageThreadScope,
  MessageThreadSummaries,
} from "@/api/dto/message";
import {
  Form,
  FormCodeBinding,
  FormTemplateRequest,
  FormTemplateResponse,
  RosterFormCodeBinding,
  UpdateFormTemplateJsonSchemaRequest
} from "@/api/dto/forms";
import {nonGroupedValues} from "@common/FilterComponent";
import {
  MessageTemplateOption,
  WorkflowDefinitionCreatedResponse,
  WorkflowDefinitionDetail,
  WorkflowDefinitionResponse,
  WorkflowSpec,
} from "@/api/dto/workflows";
import {WorkflowRoutingConfig, WorkflowRoutingConfigRequest, WorkflowRoutingDataType} from "@/api/dto/workflow-routing";
import {FilterRequest, FilterResponse} from "@/api/dto/filters";
import {AppRole} from "@/api/dto/permissions";
import {
  AvailableQueue,
  CampaignConfigCreateRequest,
  CampaignConfigResponse,
  CampaignConfigUpdateRequest,
  ConnectContactAssociateResponse,
  ConnectContactsResponse,
  ConnectContactStatus,
  QueueMappingResponse,
  SmsReplyRequest,
  SmsThreadResponse,
  VoicemailConfigRequest,
  VoicemailConfigResponse,
} from "@/api/dto/connect";
import {
  CreateSecureEmailDraftRequest,
  CreateSecureEmailDraftResponse,
  SecureEmailInbox,
} from '@/api/dto/secure-email';

const SECURE_EMAIL_REQUEST_TIMEOUT_MS = 30_000;

export class SecureEmailRequestError extends Error {
  constructor(readonly status: number) {
    super(`Secure Email request failed with status ${status}.`);
    this.name = 'SecureEmailRequestError';
  }
}

axios.interceptors.response.use(function (response) {
  sessionStorage.removeItem('last_error_trace_id');
  return response;
}, function (error: AxiosError) {
  const traceId = error.response?.headers['x-trace-id'];
  if (traceId) {
    sessionStorage.setItem('last_error_trace_id', traceId || '');
  }

  if (error.response?.status === HttpStatusCode.Unauthorized) {
    window.location.pathname = "/logout";
  } else {
    return Promise.reject(error);
  }
});

export default class CarecoApi {
  getAccessToken: () => Promise<string>;
  baseUrl: string;
  secureEmailBaseUrl: string;

  constructor(getAccessToken: () => Promise<string>) {
    this.getAccessToken = getAccessToken;
    this.baseUrl = process.env.REACT_APP_CARECO_API_BASE_URL;
    this.secureEmailBaseUrl = process.env.REACT_APP_SECURE_EMAIL_API_BASE_URL;
  }

  // Uses fetch rather than axios so a Secure Email 401 surfaces as an error instead of
  // triggering the global axios interceptor's logout redirect.
  private async secureEmailRequest<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${this.secureEmailBaseUrl}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
        ...init?.headers,
      },
      signal: AbortSignal.timeout(SECURE_EMAIL_REQUEST_TIMEOUT_MS),
    });
    if (!response.ok) {
      throw new SecureEmailRequestError(response.status);
    }
    return await response.json() as T;
  }

  async getSecureEmailInboxes(groupId: number): Promise<SecureEmailInbox[]> {
    const response = await this.secureEmailRequest<{inboxes: SecureEmailInbox[]}>(
      `/api/v1/inboxes?group_id=${encodeURIComponent(String(groupId))}`,
    );
    return response.inboxes;
  }

  async createSecureEmailDraft(request: CreateSecureEmailDraftRequest, idempotencyKey: string) {
    return this.secureEmailRequest<CreateSecureEmailDraftResponse>('/api/v1/email/draft', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify(request),
    });
  }

  async getAuthorities() {
    const response = await axios.get(this.baseUrl + "/authorities", {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: AuthoritiesResponse = response.data;

    return data;
  }

  async postAppSettings(appSettingsData: AppSetting[]) {
    return axios.post(this.baseUrl + "/app_settings",
      appSettingsData,
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      }
    );
  }

  async getAppSettings() {
    const response = await axios.get(this.baseUrl + "/app_settings", {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: AppSetting[] = response.data;

    return data;
  }

  async getRosters(params?: {
        includeInactive?: boolean,
        includeUserPermissions?: boolean,
        includeRosterPermissions?: boolean,
        name?: string,
        page?: number,
        pageSize?: number,
    }) {
    let url = `${this.baseUrl}/rosters?include_inactive=${params?.includeInactive ?? false}`;

    if (typeof params?.includeUserPermissions === 'boolean') {
      url += `&include_user_permissions=${params.includeUserPermissions}`;
    }

    if (typeof params?.includeRosterPermissions === 'boolean') {
      url += `&include_roster_permissions=${params.includeRosterPermissions}`;
    }

    if (params?.name) {
      url += `&search_name=${encodeURIComponent(params.name)}`;
    }

    if (typeof params?.page === 'number' && typeof params?.pageSize === 'number') {
      url += `&page=${params.page}&page_size=${params.pageSize}`;
    }

    const response = await axios.get(url, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: Rosters = response.data;
    const rosterOptions: Value[] = data?.data?.map(roster => {
      return {label: roster.name, value: roster.id.toString()};
    }) ?? [];
    const dataRoleOptions: Value[] = data?.data_roles?.map(role => {
      return {label: role.name, value: role.id.toString()};
    }) ?? [];
    const featureRoleOptions: Value[] = data?.feature_roles?.map(role => {
      return {label: role.name, value: role.id.toString()};
    }) ?? [];
    const allOrganizations: Value[] = data?.data?.map(roster => {
      return {label: roster.organization.name, value: roster.organization.id.toString()};
    }) ?? [];
    const organizationOptions: Value[] = Helpers.removeDuplicates(allOrganizations, 'value');

    const rosterResponse: RosterResponse = {
      data,
      rosterOptions,
      dataRoleOptions,
      featureRoleOptions,
      organizationOptions
    };

    return rosterResponse;
  }

  async getRosterOptions(): Promise<Value[]> {
    const response = await axios.get(`${this.baseUrl}/rosters/options`, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    return response.data?.map((roster: { id: number; name: string }) => ({
      label: roster.name,
      value: roster.id.toString(),
    })) ?? [];
  }

  async getTaskRosters(): Promise<Value[]> {
    const response = await axios.get(`${this.baseUrl}/care_journeys/rosters`, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    return response.data?.map((roster: { id: number; name: string }) => ({
      label: roster.name,
      value: roster.id.toString(),
    })) ?? [];
  }

  async updateGroupConfiguration(id: number, configuration: GroupConfiguration) {
    const url = `${this.baseUrl}/rosters/${id}/configuration`;

    const request: UpdateGroupConfigurationRequest = {
      configuration: configuration,
    };

    await axios.put(url, request, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });
  }

  async getPdmpCredentials(rosterId: number): Promise<PdmpCredentials> {
    const url = `${this.baseUrl}/rosters/${rosterId}/pdmp-credentials`;

    const response = await axios.get<PdmpCredentials>(url, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    return response.data;
  }

  async updatePdmpCredentials(rosterId: number, credentials: UpdatePdmpCredentialsRequest): Promise<void> {
    const url = `${this.baseUrl}/rosters/${rosterId}/pdmp-credentials`;

    await axios.put(url, credentials, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });
  }

  async updateOrganizationConfiguration(id: number, configuration: OrganizationConfiguration) {
    const url = `${this.baseUrl}/organizations/${id}/configuration`;

    const request: UpdateOrganizationConfiguration = {
      configuration: configuration,
    };

    await axios.put(url, request, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });
  }

  async getClients(
    rosterId: number,
    start: number,
    searchName?: string | null,
    searchClientId?: string | null,
    userAssignedIds?: string[],
    events?: string[],
    startDate?: string | null,
    endDate?: string | null,
    sortDirection?: string | null,
    sortField?: string,
    admissionStatus?: string | null) {
    const requestBody = {
      start,
      searchName,
      searchClientId,
      userAssignedIds,
      sortField,
      sortDirection,
      events,
      startDate,
      endDate,
      admissionStatus,
    };

    const url = `${this.baseUrl}/rosters/${rosterId}/clients`;

    const response = await axios.post(url, requestBody, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: Clients = response.data;

    return {
      id: start,
      rosterId: rosterId,
      data,
      nextPage: start + 1
    };
  }

  async getClientProfile(patientId: string, rosterId: number, viewing: boolean, pageNum: number, forceRefresh: boolean = false) {
    let url = `${this.baseUrl}/client_profile?` +
            `patient_id=${patientId}&roster_id=${rosterId}&viewing=${viewing}&page=${pageNum}&cb=${Date.now()}`;

    if (forceRefresh) {
      url += `&force_refresh=true`;
    }

    const response = await axios.get(url, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: ClientProfileResponse = response.data;

    return data;
  }

  async deleteJIEvent(patientId: string | undefined, bookingId: number) {
    const url = `${this.baseUrl}/client_profile/ji_event` +
            `/${patientId}/${bookingId}`;

    return await axios.delete(url, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async updateClientProfile(patientId: string, rosterId: number, data: ClientProfileUpdate) {
    if (data.clientId?.length === 0) { // Ping doesn't reject empty strings, so this will create conflicts
      data.clientId = null;
    }

    const formData = new FormData();
    formData.append('data', JSON.stringify({...data, photo: undefined}));

    if (data.photo) {
      formData.append('file', data.photo);
    }

    const url = `${this.baseUrl}/client_profile?patient_id=${patientId}&roster_id=${rosterId}`;

    return axios.put<ClientProfile>(encodeURI(url),
      formData,
      {
        headers: {
          Authorization: `Bearer ${await this.getAccessToken()}`,
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  }

  async updateClientPhoto(patientId: string, rosterId: number, photo: Blob) {
    const formData = new FormData();
    formData.append('file', photo);

    const url = `${this.baseUrl}/client_profile/photo?patient_id=${patientId}&roster_id=${rosterId}`;

    return axios.put(encodeURI(url),
      formData,
      {
        headers: {
          Authorization: `Bearer ${await this.getAccessToken()}`,
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  }

  async getDischargeSummary(patientId: string, encounterId: string, rosterId: number, contentType: string) {
    const url = `${this.baseUrl}/client_profile/discharge_summary`
            + `?patient_id=${patientId}&roster_id=${rosterId}&encounter_id=${encounterId}&content_type=${contentType}`;

    const response = await axios.get(url, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: string = response.data;

    return data;
  }

  async getCareJourneys(page?: number, filters?: CareJourneyFilters) {
    const params = new URLSearchParams();
    if (typeof page === 'number') params.append('page', page.toString());
    const url = `${this.baseUrl}/care_journeys?${params.toString()}`;

    const body: CareJourneyFilters = {
      ...filters,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    const response = await axios.post(url, body, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: CareJourneyResponses = response.data;

    return {
      id: page,
      data,
      nextPage: (page ?? 1) + 1,
    };
  }

  async getPreReviewCareJourneys(page?: number, filters?: CareJourneyFilters) {
    const params = new URLSearchParams();
    if (typeof page === 'number') params.append('page', page.toString());
    const url = `${this.baseUrl}/care_journeys/pre_review?${params.toString()}`;

    const body: CareJourneyFilters = {
      ...filters,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    const response = await axios.post(url, body, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: CareJourneyResponses = response.data;

    return {
      id: page,
      data,
      nextPage: (page ?? 1) + 1,
    };
  }

  async getTaskSummary(request: TaskSummaryRequest) {
    const url = `${this.baseUrl}/care_journeys/summary`;

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const body: TaskSummaryRequest = {
      ...request,
      pinnedFilters: (request.pinnedFilters ?? []).map((pinnedFilter) => ({
        ...pinnedFilter,
        filters: { ...pinnedFilter.filters, timeZone },
      })),
    };
    const response = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: TaskSummaryResponse = response.data;
    return data;
  }

  async getCareJourneyAssignmentOptions() {
    const url = `${this.baseUrl}/users/task_assigned`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: User[] = response.data;

    const options: nonGroupedValues[] = data.map(user => {
      return {
        label: DisplayHelpers.formatName(user.first_name, null, user.last_name),
        id: user.id,
        value: user.id
      };
    });

    return [
      {label: "Automated Agent", value: "AUTOMATED_AGENT", id: "AUTOMATED_AGENT"},
      {label: "Unassigned", value: "UNASSIGNED", id: "UNASSIGNED"},
      ...options
    ];
  }

  async getCareJourney(careJourneyId: string, rosterId: number) {
    const url = `${this.baseUrl}/care_journeys/${careJourneyId}?roster_id=${rosterId}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: CareJourneyDetailsResponse = response.data;
    return data;
  }

  async getAdditionalEvents(careJourneyId: string, rosterId: number) {
    const url = `${this.baseUrl}/care_journeys/${careJourneyId}/additional_events?roster_id=${rosterId}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: AdditionalEvent[] = response.data;
    return data;
  }

  async createScheduledAiAppointment(request: ScheduleAppointmentRequest, rosterId: number) {
    const url = `${this.baseUrl}/care_journeys/${request.parentJourneyId}/appointment/initiate?roster_id=${rosterId}`;
    const response = await axios.post(encodeURI(url), request, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: ScheduleAppointmentResponse = response.data;
    return data;
  }

  async createScheduledCareNavigatorAppointment(request: ScheduleAppointmentRequest, rosterId: number) {
    const url = `${this.baseUrl}/care_journeys/${request.parentJourneyId}/appointment/record?roster_id=${rosterId}`;
    const response = await axios.post(encodeURI(url), request, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: ScheduleAppointmentResponse = response.data;
    return data;
  }

  async getCareJourneyStepForm(step: string, rosterId: number, journeyType?: string, careJourneyId?: string) {
    let url = `${this.baseUrl}/form_templates/care_journey/step/${step}?roster_id=${rosterId}`;

    if(journeyType) {
      url += `&journey_type=${journeyType}`;
    }

    if(careJourneyId) {
      url += `&care_journey_id=${careJourneyId}`;
    }

    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: string = response.data;
    return data;
  }

  async createCareJourneyAttachment(careJourneyId: string, rosterId: number, formData: FormData) {
    const url = `${this.baseUrl}/care_journeys/${careJourneyId}/attachments?roster_id=${rosterId}`;
    return await axios.post(encodeURI(url),
      formData,
      {
        headers: {
          Authorization: `Bearer ${await this.getAccessToken()}`,
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  }

  async getCareJourneyAttachment(careJourneyAttachmentId: string, rosterId: number) {
    const url = `${this.baseUrl}/care_journeys/attachments/${careJourneyAttachmentId}?roster_id=${rosterId}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: string = response.data;
    return data;
  }

  async deleteCareJourneyAttachment(careJourneyAttachmentId: string, rosterId: number) {
    const url = `${this.baseUrl}/care_journeys/attachments/${careJourneyAttachmentId}?roster_id=${rosterId}`;
    return await axios.delete(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async getCareNotePreview(careJourneyId: string, rosterId: number): Promise<DmcsGenerateDocumentResponseContext> {
    const url = `${this.baseUrl}/care_journeys/${careJourneyId}/care_note/preview?roster_id=${rosterId}`;
    const response = await axios.get<DmcsGenerateDocumentResponseContext>(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
    return response.data;
  }

  async getCareJourneyReferralPreview(referralId: string): Promise<ReferralAttachmentResponse> {
    const url = `${this.baseUrl}/care_journeys/pre_review/attachment?referral_id=${referralId}`;
    const response = await axios.get<ReferralAttachmentResponse>(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
    return response.data;
  }

  async sendCareTeamReferral(careJourneyId: string, rosterId: number) {
    const url = `${this.baseUrl}/care_journeys/${careJourneyId}/care_team_referral?roster_id=${rosterId}`;
    const response = await axios.post(encodeURI(url), null, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
    return response.data;
  }
  async sendCareNote(careJourneyId: string, rosterId: number) {
    const url = `${this.baseUrl}/care_journeys/${careJourneyId}/care_note/send?roster_id=${rosterId}`;
    const response = await axios.post(encodeURI(url), null, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
    return response.data;
  }

  async escalateCareJourney(careJourneyId: string, rosterId: number, escalatedToCareTeam: boolean, reason?: string | null) {
    const url = `${this.baseUrl}/care_journeys/${careJourneyId}/escalation?roster_id=${rosterId}`;
    const response = await axios.put(encodeURI(url), { escalatedToCareTeam, reason }, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
    return response.data;
  }

  async updateCareJourney(careJourneyId: string, rosterId: number, data: CareJourneyRequest) {
    const url = `${this.baseUrl}/care_journeys/${careJourneyId}?roster_id=${rosterId}`;
    return axios.put(encodeURI(url),
      data,
      {
        headers: {
          Authorization: `Bearer ${await this.getAccessToken()}`,
        },
      },
    );
  }

  async submitCareJourneyAttestation(careJourneyId: string, rosterId: number) {
    const url = `${this.baseUrl}/care_journeys/${careJourneyId}/attestation?roster_id=${rosterId}`;
    const response = await axios.post(encodeURI(url), null, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
    return response.data;
  }

  async submitCareJourneyStepData(careJourneyId: string, rosterId: number, data: string, stepGuid: string) {
    const url = this.baseUrl + `/care_journeys/${careJourneyId}/step_data?roster_id=${rosterId}`;
    await axios.post(encodeURI(url),
      {
        step_guid: stepGuid,
        step_data: data
      },
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      }
    );
  }

  async saveCareJourneyStepDraft(careJourneyId: string, rosterId: number, schemaGuid: string, data: any) {
    const url = this.baseUrl + `/care_journeys/${careJourneyId}/steps/${schemaGuid}/draft?roster_id=${rosterId}`;
    await axios.put(encodeURI(url),
      JSON.stringify(data),
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
          "Content-Type": "application/json"
        }
      }
    );
  }

  async sealAppointment(careJourneyId: string, rosterId: number, request: SealAppointmentRequest) {
    const url = `${this.baseUrl}/care_journeys/${careJourneyId}/appointment/seal?roster_id=${rosterId}`;
    const response = await axios.post(encodeURI(url), request, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
    return response.data;
  }

  async cancelAppointment(careJourneyId: string, rosterId: number, request: CancelAppointmentRequest) {
    const url = `${this.baseUrl}/care_journeys/${careJourneyId}/appointment/cancel?roster_id=${rosterId}`;
    const response = await axios.post(encodeURI(url), request, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
    return response.data;
  }

  async submitClientWorkflowStep(
    careJourneyId: string, rosterId: number,
    moduleId: string, taskId: string, formJson: string
  ) {
    const url = this.baseUrl
      + `/care_journeys/${careJourneyId}/cwe/step_data?roster_id=${rosterId}`;
    await axios.post(url,
      { moduleId, taskId, formJson },
      { headers: { "Authorization": `Bearer ${await this.getAccessToken()}` } }
    );
  }

  // CARECO-5758: confirm the pre-review — persists reviewer edits, marks the referral CONFIRMED,
  // and completes the CARE_JOURNEY_PRE_REVIEW USER_TASK (advancing to INITIATE_CARE_JOURNEY).
  async confirmPreReview(
    careJourneyId: string, rosterId: number, request: ConfirmPreReviewRequest
  ) {
    const url = this.baseUrl
      + `/care_journeys/${careJourneyId}/cwe/pre-review/confirm?roster_id=${rosterId}`;
    await axios.post(url,
      request,
      { headers: { "Authorization": `Bearer ${await this.getAccessToken()}` } }
    );
  }

  // CARECO-5767/5768: reject the pre-review — records the rejection reason, marks the referral
  // REJECTED, and completes the CARE_JOURNEY_PRE_REVIEW USER_TASK (no care journey is created).
  async rejectReferral(
    careJourneyId: string, rosterId: number, request: RejectPreReviewRequest
  ) {
    const url = this.baseUrl
      + `/care_journeys/${careJourneyId}/cwe/pre-review/reject?roster_id=${rosterId}`;
    await axios.post(url,
      request,
      { headers: { "Authorization": `Bearer ${await this.getAccessToken()}` } }
    );
  }

  // CARECO-5760: save reviewer edits as a draft — persists the working set to referral_review_data
  // WITHOUT completing the CARE_JOURNEY_PRE_REVIEW USER_TASK, so the review can be resumed later.
  async saveReviewData(
    careJourneyId: string, rosterId: number, request: ReviewDataEditRequest
  ) {
    const url = this.baseUrl
      + `/care_journeys/${careJourneyId}/cwe/pre-review/review-data?roster_id=${rosterId}`;
    await axios.put(url,
      request,
      { headers: { "Authorization": `Bearer ${await this.getAccessToken()}` } }
    );
  }

  async getOrganizations() {
    const response = await axios.get(this.baseUrl + "/organizations", {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: Organization[] = response.data;

    return data;
  }

  async putUser(userId: string, userData: UserRequest | UserRequestNew) {
    const url = this.baseUrl + `/users/${userId}`;
    return axios.put(encodeURI(url),
      userData,
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      }
    );
  }

  async patchUser(userId: string, userPatchData: UserPatchRequest) {
    const url = `${this.baseUrl}/users/${userId}`;
    const response = await axios.patch(encodeURI(url),
      userPatchData,
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
        },
      },
    );

    const data: User = response.data;

    return data;
  }

  async putUserProfile(userId: string, userProfileData: UserProfileRequest) {
    const url = this.baseUrl + `/users/${userId}/profile`;
    return axios.put(encodeURI(url),
      userProfileData,
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      }
    );
  }

  async getUsers(params?: {
        name?: string;
        statuses?: string[];
        featureRoles?: string[];
        dataRoles?: string[];
        organizations?: string[];
        groups?: string[];
        sortBy?: string;
        sortOrder?: string;
        page?: number;
        pageSize?: number;
        sso?: string[];
        roles?: AppRole[]
    }) {
    const page = params?.page ?? 1;
    let url = `${this.baseUrl}/users/search`;

    const queryParams: string[] = [];
    if (typeof params?.page === 'number') {
      queryParams.push(`page=${params.page}`);
    }
    if (typeof params?.pageSize === 'number') {
      queryParams.push(`page_size=${params.pageSize}`);
    }
    if (queryParams.length) {
      url += `?${queryParams.join('&')}`;
    }

    const body = {
      search_name: params?.name,
      statuses: params?.statuses?.length ? params.statuses : null,
      feature_roles: params?.featureRoles?.length ? params.featureRoles : null,
      data_roles: params?.dataRoles?.length ? params.dataRoles : null,
      organizations: params?.organizations?.length ? params.organizations : null,
      groups: params?.groups?.length ? params.groups : null,
      sso: params?.sso?.length ? params.sso : null,
      roles: params?.roles?.length ? params.roles : null,
      sort_by: params?.sortBy,
      sort_order: params?.sortOrder,
    };

    const response = await axios.post(url, body, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: Users = response.data;

    return {
      id: page,
      data,
      nextPage: page + 1,
    };
  }

  async getAssignableUsers(params: {
    rosterId?: number;
    name?: string;
    page?: number;
    pageSize?: number;
  }) {
    const page = params.page ?? 1;

    const queryParams: string[] = [];
    if (typeof params.rosterId === 'number') {
      queryParams.push(`roster_id=${params.rosterId}`);
    }
    if (typeof params.page === 'number') {
      queryParams.push(`page=${params.page}`);
    }
    if (typeof params.pageSize === 'number') {
      queryParams.push(`page_size=${params.pageSize}`);
    }

    let url = `${this.baseUrl}/users/assignable`;
    if (queryParams.length) {
      url += `?${queryParams.join('&')}`;
    }

    const response = await axios.post(url, { search_name: params.name }, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const body: Users = response.data;

    const data: Users = {
      ...body,
      users: (body.users ?? []).map((user) => ({
        ...user,
        id: (user as User & { user_id?: string }).user_id ?? user.id,
      })),
    };

    return {
      id: page,
      data,
      nextPage: page + 1,
    };
  }

  async getUsersExport(params?: {
        name: string;
        statuses?: string[];
        featureRoles?: string[];
        dataRoles?: string[];
        organizations?: string[];
        groups?: string[];
        sso?: string[];
        roles?: AppRole[]
    }): Promise<{data: Blob}> {
    const url = `${this.baseUrl}/users/export`;

    const body = {
      search_name: params?.name,
      statuses: params?.statuses?.length ? params.statuses : null,
      feature_roles: params?.featureRoles?.length ? params.featureRoles : null,
      data_roles: params?.dataRoles?.length ? params.dataRoles : null,
      organizations: params?.organizations?.length ? params.organizations : null,
      groups: params?.groups?.length ? params.groups : null,
      sso: params?.sso?.length ? params.sso : null,
      roles: params?.roles?.length ? params.roles : null,
    };

    const response = await axios.post(url, body, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
      responseType: 'blob'
    });

    return {
      data: response.data
    };
  }

  async getUsersAssignedToClients(rosterId?: number) {
    let url = `${this.baseUrl}/users/client_assigned`;

    if (rosterId) {
      url += `?roster_id=${rosterId}`;
    }

    const response = await axios.get(url, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: User[] = response.data;

    return data;
  }

  async getUser(userId: string) {
    const url = this.baseUrl + `/users/${userId}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: User = response.data;

    return data;
  }

  async postCreateUser(createUserRequest: UserRequest | UserRequestNew) {
    const response = await axios.post(this.baseUrl + "/users", createUserRequest, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: { id: string } = response.data;
    return data;
  }

  async deleteUser(userId: string) {
    const url = this.baseUrl + `/users/${userId}/delete`;
    await axios.delete(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });
  }

  async login() {
    const response = await axios.get(`${this.baseUrl}/login`, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: LoginResponse = response.data;

    return data;
  }

  async getLoginPrompts() {
    const response = await axios.get(`${this.baseUrl}/attestation/login_prompts`, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: LoginResponse = response.data;

    return data;
  }

  async postAttestation(version: string) {
    return axios.post(this.baseUrl + "/attestation", {
      'document_version': version
    }, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async getSurveyJSLicenseKey() {
    const response = await axios.get(`${this.baseUrl}/login/surveyjs`, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: SurveyJSLicenseKey = response.data;
    return data;
  }

  async getBackendVersions(): Promise<BackendVersions> {
    const response = await axios.get(this.baseUrl + "/version");
    return response.data;
  }

  async getRosterDetails(rosterId: string | null) {
    if (rosterId) {
      const response = await axios.get(this.baseUrl + "/roster_details/" + rosterId, {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      });

      const data: RosterPermissions = response.data.rosterPermissions;
      return data;
    }
  }

  async getNotificationSetting() {
    const url = this.baseUrl + `/notification_settings`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: NotificationSettingsDto = response.data;
    return NotificationHelpers.buildNotificationSettings(data);
  }

  async putNotificationSetting(notificationSettingsResponse: NotificationSettingsDto) {
    const url = this.baseUrl + `/notification_settings`;

    return axios.put(encodeURI(url),
      notificationSettingsResponse,
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      }
    );
  }

  async postClientUserAssignment(patientId: string, userId: string, rosterId: number) {
    return await axios.post(`${this.baseUrl}/clients/user_assignment`, {
      patient_id: patientId,
      user_id: userId,
      roster_id: rosterId
    }, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async deleteClientUserAssignment(patientId: string, userId: string, rosterId: number) {
    const url = `${this.baseUrl}/clients/user_assignment?patient_id=${patientId}&user_id=${userId}&roster_id=${rosterId}`;

    return await axios.delete(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async searchPatientByName(client: ClientGroup) {
    const response = await axios.post(this.baseUrl + "/clients/search", client, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: EncounterClient[] = await response.data;
    return data;
  }

  async postFormUpload(form: FormData) {
    const url = `${this.baseUrl}/client_profile/forms/upload`;

    const response = await axios.post(url,
      form,
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
          "Content-Type": "multipart/form-data"
        }
      }
    );

    const data: { id: number } = response.data;
    return data;
  }

  async getFormUpload(rosterId: number, formRosterId: number, patientId: string, formId: number) {
    const url = `${this.baseUrl}/client_profile/forms/upload`
            + `?roster_id=${rosterId}&form_patient_id=${patientId}&form_roster_id=${formRosterId}&form_id=${formId}`;

    const response = await axios.get(url, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    return response.data;
  }

  async deleteForm(formId: number) {
    const url = `${this.baseUrl}/client_profile/forms/${formId}`;

    return await axios.delete(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async getForms(rosterId: number, patientId: string) {
    const url = `${this.baseUrl}/client_profile/forms`
            + `?patient_id=${patientId}&roster_id=${rosterId}`;

    const response = await axios.get(url, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: Form[] = response.data;

    return data;
  }

  async postTemplateForm(
    rosterId: number,
    clientId: string,
    formTemplateId: number,
    json: string,
    completed?: boolean,
    encounterId?: string,
  ) {
    await axios.post(`${this.baseUrl}/client_profile/forms/template`,
      {
        roster_id: rosterId,
        client_id: clientId,
        encounter_id: encounterId,
        form_template_id: formTemplateId,
        json,
        completed,
      },
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
        },
      },
    );
  }

  async putTemplateForm(
    rosterId: number,
    clientId: string,
    formId: number,
    json: string,
    completed?: boolean,
    edited?: boolean,
  ) {
    await axios.put(`${this.baseUrl}/client_profile/forms/template/${formId}`,
      {
        roster_id: rosterId,
        client_id: clientId,
        json,
        completed,
        edited
      },
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
        },
      },
    );
  }

  async refreshTemplateFormToLatestVersion(
    rosterId: number,
    clientId: string,
    formId: number,
  ) {
    await axios.post(`${this.baseUrl}/client_profile/forms/template/${formId}/refresh`,
      {
        roster_id: rosterId,
        client_id: clientId,
      },
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
        },
      },
    );
  }

  async getTemplateForm(rosterId: number, clientId: string, formId: number) {
    const url = `${this.baseUrl}/client_profile/forms/template/${formId}?roster_id=${rosterId}&client_id=${clientId}`;

    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: Record<string, unknown> = response.data;
    return data;
  }

  async getFormByEncounterAndCodeBinding(encounterId: string, codeBinding: FormCodeBinding) {
    const url = `${this.baseUrl}/client_profile/forms/encounter/${encounterId}/code_binding/${codeBinding}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`,
      },
    });
    const data: Record<string, unknown> = response.data;
    return data;
  }

  async getFormTemplateByCodeBinding(codeBinding: FormCodeBinding) {
    const url = `${this.baseUrl}/form_templates/code_binding/${codeBinding}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`,
      },
    });
    const data: FormTemplateResponse = response.data;
    return data;
  }

  async getFormTemplateByRosterCodeBinding(codeBinding: RosterFormCodeBinding, rosterId: number) {
    const url = `${this.baseUrl}/form_templates/roster/${rosterId}/code_binding/${codeBinding}`;

    return await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`,
      },
    }).then(res => {
      return res.data as FormTemplateResponse;
    }).catch(error => {
      if (axios.isAxiosError(error) && error.response?.status !== HttpStatusCode.NotFound) {
        throw error;
      }
      return null;
    });
  }

  async getFormByRosterCodeBindingAndPatientId(codeBinding: RosterFormCodeBinding, patientId: string, rosterId: number) {
    const url = `${this.baseUrl}/forms/roster/${rosterId}/patient/${patientId}/code_binding/${codeBinding}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`,
      },
    });
    const data: Form = response.data;
    return data;
  }

  async getSurveyFormData(formId: number, rosterId: number) {
    const url = `${this.baseUrl}/forms/${formId}/roster/${rosterId}/survey_data`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`,
      },
    });
    const data: string = response.data;
    return data;
  }

  async redirectToOpenBedsSSO(rosterId?: number) {
    const response = await axios.post(this.baseUrl + `/sso/open_beds/redirect`,
      {},
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
          "Careco-Roster-Id": rosterId
        }
      }
    );
    const data: string = response.data;
    return data;
  }

  async getFindHelpUrl(rosterId: number, patientId: string) {
    const url = `${this.baseUrl}/sso/find_help/url?patient_id=${patientId}`;

    const response = await axios.get(encodeURI(url),
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
          "Careco-Roster-Id": rosterId,
        },
      },
    );

    const data: string = response?.data.url;

    return data;
  }

  async readSettings(groupId: number) {
    const url = `${this.baseUrl}/encounters_resources/read_settings?group_id=${groupId}`;

    const response = await axios.get(url, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: string[] = response.data;

    return data.sort().map((setting) => ({
      value: setting,
      label: DisplayHelpers.humanize(setting),
      id: setting.replace(' ', '-').toLowerCase(),
    }));
  }

  async readDischargeDispositions(includeExtended: boolean) {
    const url = `${this.baseUrl}/encounters_resources/read_discharge_dispositions?include_extended=${includeExtended}`;

    const response = await axios.get(url, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: DischargeDisposition[] = response.data;

    return data.map((disposition) => ({
      ...disposition,
      value: disposition.dispositionSynonym,
      label: disposition.dispositionSynonym,
    }))
      .sort((a, b) => {
        if (a.dispositionSynonym < b.dispositionSynonym) return -1;
        if (a.dispositionSynonym > b.dispositionSynonym) return 1;
        return 0;
      });
  }

  async searchFacilities(name: string, facilityDispositionType?: string, groupId?: number) {
    let url = `${this.baseUrl}/encounters_resources/search_facilities?name=${name}`;

    if (facilityDispositionType) {
      url += `&facility_disposition_type=${facilityDispositionType}`;
    }

    if (groupId) {
      url += `&group_id=${groupId}`;
    }

    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: SearchFacility[] = response.data;

    return data.map((facility) => ({
      id: facility.id,
      label: facility.name,
    }));
  }

  async readInsurers() {
    const url = `${this.baseUrl}/encounters_resources/read_insurers`;

    const response = await axios.get(url, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: Insurance[] = response.data;

    const insurers = data.map((insurance) => {
      const insuranceVar = {...insurance};
      const insuranceId = insuranceVar.insurerId;
      const insuranceCompanyName = insuranceVar.companyName;

      delete insuranceVar.companyName;

      return {
        ...insuranceVar,
        id: insuranceId,
        value: insuranceId,
        label: insuranceCompanyName,
        insuranceCompanyName,
      };
    });
    insurers.push(
      // @ts-expect-error - we don't have to initialize all the fields
      ...[
        {
          code: 'OTHER',
          insuranceCompanyName: 'Other',
          label: 'Other',
          insurancePlanId: 0,
          insurerId: 0,
          value: '',
        },
        {
          code: 'SELF-PAY',
          insuranceCompanyName: 'Self Pay',
          label: 'Self Pay',
          insurancePlanId: 0,
          insurerId: 0,
          value: '',
        },
      ],
    );
    return insurers
      .filter((insurance) => insurance.insuranceCompanyName !== undefined)
      .sort((a, b) => {
        return a.insuranceCompanyName?.localeCompare(b.insuranceCompanyName || "") || 0;
      }) as unknown as InsurancePlan[];
  }

  async getNotifications(unreadOnly: boolean, page: number, rosterId?: number | null) {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('unread_only', unreadOnly.toString());
    if (typeof rosterId === 'number') {
      params.append('roster_id', rosterId.toString());
    }

    const url = `${this.baseUrl}/notifications?${params.toString()}`;

    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: NotificationResponse = response.data;

    return {
      id: page,
      data,
      nextPage: data.hasMore ? page + 1 : undefined
    };
  }

  async putNotificationViewed(notificationId: number, viewed: boolean) {
    const url = this.baseUrl + `/notifications/${notificationId}`;
    return axios.put(encodeURI(url),
      {viewed: viewed},
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      }
    );
  }

  async putAllNotificationsAsViewed(rosterId?: number) {
    const url = rosterId
      ? this.baseUrl + `/notifications/rosters/${rosterId}/viewed`
      : this.baseUrl + `/notifications/viewed`;
    return axios.put(encodeURI(url),
      {},
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      }
    );
  }

  async getPatientRequest(rosterId: number, patientId: string) {
    const url = `${this.baseUrl}/pdmp/patient_request?roster_id=${rosterId}&patient_id=${patientId}`;

    const response = await axios.get(encodeURI(url),
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      }
    );

    const data: PatientResponse = response.data;
    return data;
  }

  async getReportRequest(rosterId: number, patientId: string, reportRequestUrl: string) {
    let url = `${this.baseUrl}/pdmp/report_request`;
    url += `?roster_id=${rosterId}&patient_id=${patientId}&report_request_url=${reportRequestUrl}`;

    const response = await axios.get(encodeURI(url),
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      }
    );

    return `${this.baseUrl}/pdmp/report?roster_id=${rosterId}&report_id=${response.data}`;
  }

  async getWalkMeData(user_id: string) {
    const url = `${this.baseUrl}/walk-me/${user_id}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: WalkMeData = response.data;
    return data;
  }

  async postScanRosterTool(rosterId: number) {
    const url = `${this.baseUrl}/tools/scan_roster/${rosterId}`;
    const response = await axios.post(
      encodeURI(url),
      null,
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      });

    const data: ScanRosterResponse = response.data;
    return data;
  }

  async getScanRosterTool() {
    const url = `${this.baseUrl}/tools/scan_roster`;
    const response = await axios.get(
      encodeURI(url),
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      });

    const data: ScanRosterResponse = response.data;
    return data;
  }

  async postBulkUserUploadTool(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseUrl}/tools/bulk_user_upload`;
    const response = await axios.post(
      encodeURI(url),
      formData,
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
        },
      },
    );

    const data: BulkUserUploadResponse = response.data;
    return data;
  }

  async getBulkUserUploadTool() {
    const url = `${this.baseUrl}/tools/bulk_user_upload`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: BulkUserUploadData[] = response.data;
    return data;
  }

  async getBulkUserUploadToolFile(bulkUserUploadId: number, results: boolean) {
    const url = `${this.baseUrl}/tools/bulk_user_upload/${bulkUserUploadId}?results=${results}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: string = response.data;
    return data;
  }

  async postDiagnosisCodesUpdateTool() {
    const url = `${this.baseUrl}/tools/diagnosis_codes_update`;
    const response = await axios.post(
      encodeURI(url),
      null,
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
        },
      },
    );

    const data: DiagnosisCodesUpdateResponse = response.data;
    return data;
  }

  async assignToAi(journeyId: string, rosterId: number) {
    const url = `${this.baseUrl}/care_journeys/${journeyId}/assign_ai?roster_id=${rosterId}`;
    const response = await axios.post(
      encodeURI(url),
      null,
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
        },
      },
    );

    const data: string = response.data;
    return data;
  }

  async postMixpanelEvent(event: MixpanelEvent) {
    const url = `${this.baseUrl}/mixpanel`;
    await axios.post(encodeURI(url),
      event, {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      });
  }

  async getAdminDetails() {
    const url = this.baseUrl + `/users/admin_details`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
    const data: AdminDetailByRoster[] = response.data;
    return data;
  }

  // this request provides generic HttpOnly cookie which is used for authorization
  // in parts of the system that can't utilize Authorization header
  async getFeatureToggles() {
    const url = `${this.baseUrl}/togglz`;
    const response = await axios.get(encodeURI(url), {
      withCredentials: true,
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
    const data: FeatureToggleResponse = response.data;
    return data;
  }

  async getAnalyticsTicket() {
    const url = `${this.baseUrl}/analytics/ticket`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`,
      },
    });
    return response.data.ticket as string;
  }

  async getStateConfig(rosterId?: number) {
    let url = `${this.baseUrl}/states/config`;

    if (rosterId) {
      url += `?roster_id=${rosterId}`;
    }

    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`,
      },
    });
    const data: StateConfig = response.data;
    return data;
  }

  async getAllEventConsumers() {
    const url = this.baseUrl + '/event_consumers';
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
    const data: EventConsumer[] = response.data;
    return data;
  }

  async getEventConsumer(id: number) {
    const url = `${this.baseUrl}/event_consumers/${id}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
    const data: EventConsumer = response.data;
    return data;
  }

  async createEventConsumer(payload: EventConsumer[]) {
    const url = `${this.baseUrl}/event_consumers`;
    await axios.post(encodeURI(url), payload, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async updateEventConsumer(payload: EventConsumer) {
    const url = `${this.baseUrl}/event_consumers`;
    await axios.put(encodeURI(url), payload, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async searchDiagnosisCodes(search: string) {
    const url = `${this.baseUrl}/diagnosis_codes?search_name=${search}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
    const data: DiagnosisCodeResponse[] = response.data;
    return data;
  }

  async getMessageThreadSummaries(
    rosterId: number,
    clientId: string,
    careJourneyInstanceId: string | null,
    referralId: string | null,
    unitId: string | null,
    participantOnly?: boolean,
    scope?: MessageThreadScope,
    search?: string
  ) {
    const params = new URLSearchParams();
    params.append('roster_id', rosterId.toString());
    params.append('client_id', clientId);

    if (careJourneyInstanceId?.length) {
      params.append('care_journey_instance_id', careJourneyInstanceId);
    }

    if (referralId?.length) {
      params.append('referral_id', referralId);
    }

    if (unitId?.length) {
      params.append('unit_id', unitId);
    }

    if (typeof participantOnly === 'boolean') {
      params.append('participant_only', participantOnly.toString());
    }

    if (search?.length) {
      params.append('search_name', search);
    }

    if (scope?.length) {
      params.append('scope', scope);
    }

    const url = `${this.baseUrl}/message_threads?${params.toString()}`;

    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: MessageThreadSummaries = response.data;
    return data;
  }

  async getMessageThreadInbox(unreadOnly: boolean, page: number, search?: string, threadType?: string, rosterId?: number) {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('unread_only', unreadOnly.toString());
    if (search?.length) {
      params.append('search_name', search);
    }
    if (threadType && threadType !== 'All') {
      params.append('thread_type', threadType);
    }
    if (rosterId) {
      params.append('roster_id', rosterId.toString());
    }

    const url = `${this.baseUrl}/message_threads/inbox?${params.toString()}`;

    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`
      },
    });

    const data: MessageThreadSummaries = response.data;

    return {
      id: page,
      data,
      nextPage: data.hasMore ? page + 1 : undefined,
    };
  }

  async getMessageThread(messageThreadId: number) {
    const url = `${this.baseUrl}/message_threads/${messageThreadId}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: MessageThread = response.data;
    return data;
  }

  async patchMessageThread(messageThreadId: number, messageThreadPatch: MessageThreadPatch) {
    const url = `${this.baseUrl}/message_threads/${messageThreadId}`;
    return axios.patch(encodeURI(url),
      messageThreadPatch,
      {
        headers: {
          Authorization: `Bearer ${await this.getAccessToken()}`,
        },
      },
    );
  }

  async createMessageAttachments(clientId: string, attachments: File[]) {
    const formData = new FormData();
    for (const attachment of attachments) {
      formData.append('files', attachment);
    }

    const params = new URLSearchParams();
    params.append('client_id', clientId);
    const url = `${this.baseUrl}/message_threads/attachments?${params.toString()}`;
    const response = await axios.put(encodeURI(url),
      formData,
      {
        headers: {
          Authorization: `Bearer ${await this.getAccessToken()}`,
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    const data: MessageAttachment[] = response.data;
    return data;
  }

  async getMessageAttachment(messageAttachmentId: number) {
    const url = `${this.baseUrl}/message_threads/attachments/${messageAttachmentId}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: string = response.data;
    return data;
  }

  async deleteMessageAttachment(messageAttachmentId: number) {
    const url = `${this.baseUrl}/message_threads/attachments/${messageAttachmentId}`;
    return axios.delete(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async readAllMessageThreads(rosterId?: number) {
    const params = new URLSearchParams();
    if (rosterId) {
      params.append('roster_id', rosterId.toString());
    }
    const url = `${this.baseUrl}/message_threads/read?${params.toString()}`;
    return axios.post(encodeURI(url), null, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async readMessageThread(messageThreadId: number, read: boolean) {
    const params = new URLSearchParams();
    params.append('read', read.toString());
    const url = `${this.baseUrl}/message_threads/${messageThreadId}/read?${params.toString()}`;
    return axios.post(encodeURI(url), null, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async readMessage(messageThreadId: number, messageId: number) {
    const url = `${this.baseUrl}/message_threads/${messageThreadId}/read/${messageId}`;
    return axios.post(encodeURI(url), null, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async getFormTemplates(rosterId?: number) {
    let url = this.baseUrl + '/form_templates';
    if (rosterId) {
      url += `?roster_id=${rosterId}`;
    }
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: FormTemplateResponse[] = response.data;
    return data;
  }

  async getWorkflowDefinitions() {
    const response = await axios.get(this.baseUrl + '/settings/workflows', {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: WorkflowDefinitionResponse[] = response.data;
    return data;
  }

  async getWorkflowDefinition(id: string) {
    const response = await axios.get(this.baseUrl + `/settings/workflows/${id}`, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: WorkflowDefinitionDetail = response.data;
    return data;
  }

  async getMessageTemplates() {
    const response = await axios.get(this.baseUrl + '/settings/message-templates', {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: MessageTemplateOption[] = response.data;
    return data;
  }

  async getWorkflowVersions(workflowKey: string) {
    const response = await axios.get(
      this.baseUrl + `/settings/workflows/${workflowKey}/versions`, {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      });

    const data: WorkflowDefinitionResponse[] = response.data;
    return data;
  }

  async createWorkflowDefinition(spec: WorkflowSpec) {
    const response = await axios.post(this.baseUrl + '/settings/workflows', spec, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: WorkflowDefinitionCreatedResponse = response.data;
    return data;
  }

  async updateWorkflowDefinition(id: string, spec: WorkflowSpec) {
    return axios.put(this.baseUrl + `/settings/workflows/${id}`, spec, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async createWorkflowVersion(workflowKey: string) {
    const response = await axios.post(
      this.baseUrl + `/settings/workflows/${workflowKey}/versions`, null, {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`
        }
      });

    const data: WorkflowDefinitionCreatedResponse = response.data;
    return data;
  }

  async publishWorkflowDefinition(id: string) {
    return axios.post(this.baseUrl + `/settings/workflows/${id}/publish`, null, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async archiveWorkflowDefinition(id: string) {
    return axios.post(this.baseUrl + `/settings/workflows/${id}/archive`, null, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async deleteWorkflowDefinition(id: string) {
    return axios.delete(this.baseUrl + `/settings/workflows/${id}`, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async getWorkflowRoutingConfigs() {
    const response = await axios.get(this.baseUrl + '/mgmt/workflow-routing', {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: WorkflowRoutingConfig[] = response.data;
    return data;
  }

  async getWorkflowRoutingDataTypes() {
    const response = await axios.get(this.baseUrl + '/mgmt/workflow-routing/data-types', {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: WorkflowRoutingDataType[] = response.data;
    return data;
  }

  async createWorkflowRoutingConfig(request: WorkflowRoutingConfigRequest) {
    const response = await axios.post(this.baseUrl + '/mgmt/workflow-routing', request, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: WorkflowRoutingConfig = response.data;
    return data;
  }

  async updateWorkflowRoutingConfig(id: number, request: WorkflowRoutingConfigRequest) {
    const response = await axios.put(this.baseUrl + `/mgmt/workflow-routing/${id}`, request, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: WorkflowRoutingConfig = response.data;
    return data;
  }

  async deleteWorkflowRoutingConfig(id: number) {
    return axios.delete(this.baseUrl + `/mgmt/workflow-routing/${id}`, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async getFormTemplate(formTemplateId: number, version?: number) {
    let url = `${this.baseUrl}/form_templates/${formTemplateId}`;
    if (version) {
      url += `?version=${version}`;
    }
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: FormTemplateResponse = response.data;
    return data;
  }

  async createFormTemplate(payload: FormTemplateRequest) {
    const url = `${this.baseUrl}/form_templates`;
    const response = await axios.post(encodeURI(url), payload, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
    const data: FormTemplateResponse = response.data;
    return data;
  }

  async updateFormTemplate(id: number, payload: FormTemplateRequest) {
    const url = `${this.baseUrl}/form_templates/${id}`;
    const response = await axios.put(encodeURI(url), payload, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
    const data: FormTemplateResponse = response.data;
    return data;
  }

  async updateFormTemplateVersion(id: number, surveyJson: string) {
    const url = `${this.baseUrl}/form_templates/${id}/versions`;
    const response = await axios.post(encodeURI(url), surveyJson, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
    const data: FormTemplateResponse = response.data;
    return data;
  }

  async updateFormTemplateJsonSchema(id: number, payload: UpdateFormTemplateJsonSchemaRequest) {
    const url = `${this.baseUrl}/form_templates/${id}/json_schema`;
    await axios.post(encodeURI(url), payload, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async generateFormTemplateJsonSchema(id: number, version?: number) {
    let url = `${this.baseUrl}/form_templates/${id}/generate_json_schema`;
    if (version) {
      url += `?version=${version}`;
    }

    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
    const data: object = response.data;
    return data;
  }

  async createFormFieldName(formName: string) {
    const url = `${this.baseUrl}/form_field_management/form_field_name`;
    const response = await axios.post(encodeURI(url), formName, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
    const data: string = response.data;
    return data;
  }

  async getFormFieldNames() {
    const url = `${this.baseUrl}/form_field_management/form_field_names`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
    const data: string[] = response.data;
    return data;
  }


  async deleteFormFieldName(formFieldName: string) {
    const url = `${this.baseUrl}/form_field_management/form_field_name/${formFieldName}`;
    return await axios.delete(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async createFormFieldCategory(formFieldCategory: string) {
    const url = `${this.baseUrl}/form_field_management/form_field_category`;
    const response = await axios.post(encodeURI(url), formFieldCategory, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
    const data: string = response.data;
    return data;
  }

  async getFormFieldCategories() {
    const url = `${this.baseUrl}/form_field_management/form_field_categories`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
    const data: string[] = response.data;
    return data;
  }

  async deleteFormFieldCategory(formFieldCategory: string) {
    const url = `${this.baseUrl}/form_field_management/form_field_category/${encodeURIComponent(formFieldCategory)}`;
    return await axios.delete(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async postTaskUserAssignment(taskId: string, userId: string, rosterId: number) {
    return await axios.post(`${this.baseUrl}/care_journeys/user_assignment`, {
      careJourneyId: taskId,
      userId,
      rosterId
    }, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async deleteTaskUserAssignment(taskId: string, userId: string, rosterId: number) {
    const url = `${this.baseUrl}/care_journeys/user_assignment?task_id=${taskId}&user_id=${userId}&roster_id=${rosterId}`;

    return await axios.delete(encodeURI(url), {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async unassignAI(careJourneyId: string, rosterId: number) {
    const url = `${this.baseUrl}/care_journeys/${careJourneyId}/unassign_ai?roster_id=${rosterId}`;

    return await axios.post(encodeURI(url), null, {
      headers: {
        "Authorization": `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async closeCareJourney(careJourneyId: string, rosterId: number | undefined, reason: string) {
    const url = `${this.baseUrl}/care_journeys/${careJourneyId}/close?roster_id=${rosterId}`;
    return await axios.put(encodeURI(url), {reason}, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`
      }
    });
  }

  async sendSms(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseUrl}/tools/sms`;
    const response = await axios.post(
      encodeURI(url),
      formData,
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
        },
      },
    );

    const data: SendSmsResponse = response.data;
    return data;
  }

  async getSendSmsStatus() {
    const url = `${this.baseUrl}/tools/sms`;

    const response = await axios.get(
      encodeURI(url),
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
        },
      },
    );

    const data: SendSmsResponse = response.data;
    return data;
  }

  async getSmsResponses() {
    const url = `${this.baseUrl}/tools/sms/responses`;

    const response = await axios.get(
      encodeURI(url),
      {
        headers: {
          "Authorization": `Bearer ${await this.getAccessToken()}`,
        },
      },
    );

    const data: SendSmsResult[] = response.data;
    return data;
  }

  async getSmsFailedFile(fileName: string) {
    const url = `${this.baseUrl}/tools/sms/failed/${fileName}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: string = response.data;
    return data;
  }

  async getDropdownItems(types?: DropdownItemType[], includeInactive?: boolean, currentValues?: string[]) {
    let url = `${this.baseUrl}/dropdown_items?`;

    if (types?.length) {
      for (const type of types) {
        url += `&type=${type}`;
      }
    }

    if (typeof includeInactive === 'boolean') {
      url += `&include_inactive=${includeInactive}`;
    }

    if (currentValues?.length) {
      for (const value of currentValues) {
        if (value) {
          url += `&current_values=${encodeURIComponent(value)}`;
        }
      }
    }

    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: DropdownItemResponse[] = response.data;
    return data;
  }

  async createDropdownItem(dropdownItem: DropdownItemResponse) {
    const url = `${this.baseUrl}/dropdown_items`;
    const response = await axios.post(encodeURI(url), dropdownItem, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: DropdownItemResponse = response.data;
    return data;
  }

  async updateDropdownItem(id: number, dropdownItem: DropdownItemResponse) {
    const url = `${this.baseUrl}/dropdown_items/${id}`;
    const response = await axios.put(encodeURI(url), dropdownItem, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: DropdownItemResponse = response.data;
    return data;
  }

  async enableDropdownItem(id: number) {
    const url = `${this.baseUrl}/dropdown_items/${id}`;
    await axios.patch(encodeURI(url), {}, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async deleteDropdownItem(id: number) {
    const url = `${this.baseUrl}/dropdown_items/${id}`;
    await axios.delete(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async migrateUserPermissions() {
    const url = `${this.baseUrl}/users/permissions/migrate`;
    await axios.post(encodeURI(url), {}, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async getThothToken() {
    const url = `${this.baseUrl}/users/thoth_token`;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${await this.getAccessToken()}`,
    };

    const override = getThothOverride();
    if (override) {
      headers['X-Thoth-Override'] = override;
    }

    const response = await axios.get(encodeURI(url), { headers });

    const data: string = response.data.token;
    return data;
  }

  async getUnits(rosterId: number) {
    const url = `${this.baseUrl}/rosters/units?roster_id=${rosterId}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: Unit[] = response.data;
    return data;
  }

  async getFacilitiesBatch(rosterIds: number[]): Promise<BatchFacilitiesResponse> {
    const url = new URL(`${this.baseUrl}/facilities`);
    rosterIds.forEach(id => url.searchParams.append('rosterIds', id.toString()));
    const response = await axios.get(url.toString(), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    return response.data as BatchFacilitiesResponse;
  }

  async getUserAccessTemplates() {
    const url = `${this.baseUrl}/users/access_templates`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: UserAccessTemplateResponse[] = response.data;

    return data;
  }

  async getFilters(view: string) {
    const url = `${this.baseUrl}/filters?view=${view}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: FilterResponse[] = response.data;
    return data;
  }

  async createFilter(filter: FilterRequest) {
    const url = `${this.baseUrl}/filters`;
    const response = await axios.post(encodeURI(url), filter, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: FilterResponse = response.data;
    return data;
  }

  async updateFilter(id: number, filter: FilterRequest) {
    const url = `${this.baseUrl}/filters/${id}`;
    const response = await axios.put(encodeURI(url), filter, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: FilterResponse = response.data;
    return data;
  }

  async deleteFilter(id: number) {
    const url = `${this.baseUrl}/filters/${id}`;
    await axios.delete(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async getIncompleteAdmissionCRFs(
    rosterIds: string[],
    assigneeIds: string[],
    crfCompleted: string[],
    sortField?: string,
    sortDirection?: string,
    page: number = 1,
    pageSize?: number,
  ) {
    let url = `${this.baseUrl}/analytics/incomplete_admission_crfs?`;

    for (const rosterId of rosterIds) {
      url += `&roster_id=${rosterId}`;
    }

    for (const assigneeId of assigneeIds) {
      url += `&assignee_id=${assigneeId}`;
    }

    for (const option of crfCompleted) {
      url += `&crf_completed=${option}`;
    }

    if (sortField && sortDirection) {
      url += `&sort_field=${sortField}&sort_direction=${sortDirection}`;
    }

    if (typeof page === 'number' && typeof pageSize === 'number') {
      url += `&page=${page}&page_size=${pageSize}`;
    }

    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: IncompleteAdmissionCRFsResponse = response.data;

    return {
      id: page,
      data,
      nextPage: page + 1,
    };
  }

  async getIncompleteDischargeCRFs(
    rosterIds: string[],
    assigneeIds: string[],
    formTypes: string[],
    sortField?: string,
    sortDirection?: string,
    page: number = 1,
    pageSize?: number,
  ) {
    let url = `${this.baseUrl}/analytics/incomplete_discharge_crfs?`;

    for (const rosterId of rosterIds) {
      url += `&roster_id=${rosterId}`;
    }

    for (const assigneeId of assigneeIds) {
      url += `&assignee_id=${assigneeId}`;
    }

    for (const formType of formTypes) {
      url += `&form_type=${formType}`;
    }

    if (sortField && sortDirection) {
      url += `&sort_field=${sortField}&sort_direction=${sortDirection}`;
    }

    if (typeof page === 'number' && typeof pageSize === 'number') {
      url += `&page=${page}&page_size=${pageSize}`;
    }

    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: IncompleteDischargeCRFsResponse = response.data;

    return {
      id: page,
      data,
      nextPage: page + 1,
    };
  }

  async getConnectContacts(rosterId: number, page: number, pageSize: number) {
    const url = `${this.baseUrl}/connect/contacts?roster_id=${rosterId}&page=${page}&page_size=${pageSize}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: ConnectContactsResponse = response.data;

    return {
      id: page,
      data,
      nextPage: page + 1,
    };
  }

  async patchConnectContactStatus(id: number, rosterId: number, status: ConnectContactStatus) {
    const url = `${this.baseUrl}/connect/contacts/${id}/status?roster_id=${rosterId}&status=${status}`;
    await axios.patch(encodeURI(url), null, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async associateContact(
    id: number,
    rosterId: number,
    clientId: string,
    associateAllFromNumber: boolean,
    addNumberPermanently: boolean,
  ) {
    const url = `${this.baseUrl}/connect/contacts/${id}/associate?roster_id=${rosterId}&client_id=${clientId}`
      + `&associate_all_from_number=${associateAllFromNumber}`
      + `&add_number_permanently=${addNumberPermanently}`;
    const response = await axios.post(encodeURI(url), null, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: ConnectContactAssociateResponse = response.data;
    return data;
  }

  async getConnectContactPresignedUrl(id: number, rosterId: number) {
    const url = `${this.baseUrl}/connect/contacts/${id}/presigned_url?roster_id=${rosterId}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: string = response.data;
    return data;
  }

  async getSmsThread(contactId: number) {
    const url = `${this.baseUrl}/connect/sms/thread?contact_id=${contactId}`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: SmsThreadResponse = response.data;
    return data;
  }

  // Sends an outbound SMS reply against an existing contact. On success returns the new contact id.
  // A 409 (reason CONVERSATION_ACTIVE) means a session for that number is currently open and the
  // turn-based reply cannot be sent yet; the caller is expected to surface that and let the axios
  // error propagate (its response body carries { reason, message }).
  async replySms(contactId: number, body: string) {
    const url = `${this.baseUrl}/connect/sms/reply`;
    const request: SmsReplyRequest = { contactId, body };
    const response = await axios.post(encodeURI(url), request, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: { contactId: string } = response.data;
    return data;
  }

  async getQueueMappings() {
    const url = `${this.baseUrl}/connect/queue-mappings`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: QueueMappingResponse[] = response.data;
    return data;
  }

  async createQueueMapping(rosterId: number, queueId: string, queueName: string) {
    const url = `${this.baseUrl}/connect/queue-mappings`;
    await axios.post(url, null, {
      params: { roster_id: rosterId, queue_id: queueId, queue_name: queueName },
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async deleteQueueMapping(rosterId: number, queueId: string) {
    const url = `${this.baseUrl}/connect/queue-mappings`;
    await axios.delete(url, {
      params: { roster_id: rosterId, queue_id: queueId },
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async getAvailableQueues() {
    const url = `${this.baseUrl}/connect/queues`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: AvailableQueue[] = response.data;
    return data;
  }

  async getCampaignConfigs() {
    const url = `${this.baseUrl}/connect/campaign/configs`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: CampaignConfigResponse[] = response.data;
    return data;
  }

  async createCampaignConfig(request: CampaignConfigCreateRequest) {
    const url = `${this.baseUrl}/connect/campaign/configs`;
    const response = await axios.post(url, request, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: CampaignConfigResponse = response.data;
    return data;
  }

  async updateCampaignConfig(id: number, request: CampaignConfigUpdateRequest) {
    const url = `${this.baseUrl}/connect/campaign/configs/${id}`;
    const response = await axios.put(url, request, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: CampaignConfigResponse = response.data;
    return data;
  }

  async deactivateCampaignConfig(id: number) {
    const url = `${this.baseUrl}/connect/campaign/configs/${id}`;
    await axios.delete(url, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });
  }

  async getSecureEmailTemplatesByRosterId(rosterId: number) {
    const url = `${this.baseUrl}/secure_email/${rosterId}/templates`;
    const response = await axios.get(encodeURI(url), {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`,
      },
    });

    const data: SecureEmailTemplate[] = response.data;
    return data;
  }

  async getVoicemailConfigs() {
    const url = `${this.baseUrl}/connect/voicemail/configs`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: VoicemailConfigResponse[] = response.data;
    return data;
  }

  async createVoicemailConfig(request: VoicemailConfigRequest) {
    const url = `${this.baseUrl}/connect/voicemail/configs`;
    const response = await axios.post(url, request, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: CampaignConfigResponse = response.data;
    return data;
  }

  async updateVoicemailConfig(request: VoicemailConfigRequest, id: number) {
    const url = `${this.baseUrl}/connect/voicemail/configs/${id}`;
    const response = await axios.put(url, request, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: VoicemailConfigResponse = response.data;
    return data;
  }

  async deleteVoicemailConfig(id: number) {
    const url = `${this.baseUrl}/connect/voicemail/configs/${id}`;
    const response = await axios.delete(url, {
      headers: {
        Authorization: `Bearer ${await this.getAccessToken()}`
      }
    });

    const data: VoicemailConfigResponse = response.data;
    return data;
  }
}
